import Api from 'entities/helpers/api';
import EverTrue from 'app';
import Decorator from 'clientDecorator';
import ProfileInteractionsSource from 'apps/profile/sources/profile-interactions-source';
import { getDateInTimezone } from 'base/new-utils';
import { fromSnakeToTitleCase } from '@evertrue/sdk';

export class InteractionFormController {
  // This is the list of fields that are sent to the API when creating or updating a DXO Ask interaction
  static dxoAskFields = [
    'id',
    'target_id',
    'interaction_type',
    'substantive',
    'solicitor',
    'text',
    'ask_date',
    'ask_amount',
    'purpose',
    'proposal_id',
  ];

  // This is the list of fields that are sent to the API when creating or updating a Comment
  static commentFields = [
    'id',
    'target_id',
    'target_type',
    'interaction_type',
    'pinned',
    'text',
    'user_mention',
    'contact_mention',
  ];

  // This is the list of fields that are sent to the API when creating or updating an Interaction
  static standardInteractionFields = [
    'id',
    'target_id',
    'secondary_target',
    'interaction_type',
    'substantive',
    'solicitor',
    'summary',
    'label',
    'text',
    'date_occurred',
    'custom_field',
    'meeting_id',
    'trip_id',
    'proposal_id',
    'purpose',
  ];

  static standardFieldsWithCustomFields = (hasCustomFields, orgCustomFields) => {
    if (hasCustomFields && orgCustomFields && orgCustomFields.length > 0) {
      const customFieldNames = orgCustomFields.map((field) => `custom_field.${field.id}`);
      return [...this.standardInteractionFields, ...customFieldNames];
    }
    return this.standardInteractionFields;
  };

  // Variables
  static dxoAskType = 'DXO Ask';
  static commentType = 'EverTrue Comment';
  static pinnedCommentReferrer = 'add-pinned-comment-action';

  // submission logic varies based on the interaction type
  // this method will determine the type and call the appropriate method
  static async submit(interaction) {
    const { interaction_type } = interaction;
    if (interaction_type === this.dxoAskType) {
      return this.submitDxoAsk(interaction);
    } else if (interaction_type === this.commentType) {
      return this.submitComment(interaction);
    }
    return this.submitInteraction(interaction);
  }

  static async submitInteraction(interaction) {
    const payload = this.buildRequestPayload(
      this.standardInteractionFields.reduce((obj, key) => {
        if (interaction.hasOwnProperty(key)) {
          obj[key] = interaction[key];
        }
        return obj;
      }, {})
    );
    const newInteraction = await this.createInteraction(payload);
    ProfileInteractionsSource.saveInteractionFromResponse(interaction.target_id, newInteraction);

    return newInteraction;
  }

  static async submitDxoAsk(interaction) {
    const payload = this.dxoAskFields.reduce((obj, key) => {
      if (interaction.hasOwnProperty(key)) {
        obj[key] = interaction[key];
      }
      return obj;
    }, {});
    const newInteraction = await this.createInteraction(payload);
    ProfileInteractionsSource.saveInteractionFromResponse(interaction.target_id, newInteraction);

    return newInteraction;
  }

  static async submitComment(interaction) {
    const payload = this.commentFields.reduce((obj, key) => {
      if (interaction.hasOwnProperty(key)) {
        obj[key] = interaction[key];
      }
      return obj;
    }, {});
    const newComment = await this.createComment(payload);
    ProfileInteractionsSource.saveCommentFromResponse(interaction.target_id, newComment);

    return newComment;
  }

  static async createInteraction(interaction) {
    const { id = null, target_id } = interaction;
    try {
      return await Api.UGC.COMMENT.request({
        urlExtend: id ? `/contact/${target_id}/${id}` : undefined,
        type: id ? 'PUT' : 'POST',
        data: JSON.stringify({
          ...interaction,
          target_type: 'CONTACT',
          target_id,
        }),
        success: (resp) => {
          EverTrue.Alert.success(
            id ? 'Success! Your interaction has been updated.' : 'Success! Your interaction has been saved.',
            this.toast(resp.id)
          );
          return resp;
        },
        error(xhr, testStatus) {
          // 503 error status and message is for index blocked writes ES related from UGC so ignoring the hard coded errorMessage
          if (xhr.status !== 503) {
            return EverTrue.Alert.error(
              id
                ? `We're sorry, an error occurred while trying to update your interaction. \
Please try again or contact support if the problem persists.`
                : `We're sorry, an error occurred while trying to save your interaction. \
Please try again or contact support if the problem persists.`
            );
          }
        },
      });
    } catch (e) {
      console.error(`Failed to submit interaction. Error: `, e);
      return null;
    }
  }

  static async createComment(interaction) {
    const { id = null, target_id } = interaction;
    // modify the comment object to remove any undefined values
    delete interaction.mentions;
    Object.keys(interaction).forEach((key) => interaction[key] === undefined && delete interaction[key]);

    try {
      return await Api.UGC.COMMENT.request({
        urlExtend: id ? `/contact/${target_id}/${id}` : undefined,
        type: id ? 'PUT' : 'POST',
        data: JSON.stringify({
          ...interaction,
          target_id: target_id,
          target_type: 'CONTACT',
          contact_id: target_id,
          interaction_type: 'EverTrue Comment',
        }),
        success: (resp) => {
          EverTrue.Alert.success('Success! Your comment has been saved.', this.toast(resp.id));
          return resp;
        },
        error(xhr) {
          // 503 error status and message is for index blocked writes ES related from UGC so ignoring the hard coded errorMessage
          if (xhr.status !== 503) {
            return EverTrue.Alert.error(`We're sorry, an error occurred while trying to save your comment. \
    Please try again or contact support if the problem persists.`);
          }
        },
      });
    } catch (e) {
      console.error(`Failed to submit comment. Error: ${e}`);
      return null;
    }
  }

  static buildRequestPayload = (interaction) => {
    return {
      ...interaction,
      label: Object.entries(interaction.labels || {}).flatMap(([key, valObject]) => ({
        name: key,
        value: valObject.value,
      })),
      custom_field: Object.entries(interaction.custom_field || {})
        .filter(([key, value]) => value !== undefined && value !== null && value !== '') // remove custom fields with no value from the payload. NOTE: false and 0 are both valid values.
        .map(([key, value]) => ({ id: Number(key), value })),
      secondary_target: (interaction.secondary_target || []).map((contact) => {
        const remote_id = Decorator.Relationships.getRemoteId(contact) || contact.remote_id;
        return { target_id: contact.id || contact.target_id, ...(remote_id && { remote_id }) };
      }),
    };
  };

  static toast = (id) => {
    const hasAction = !(window.location.pathname != null
      ? window.location.pathname.match(new RegExp(`^\\/interaction\\/${id}`))
      : undefined);
    const actionCallback = () => EverTrue.Navigator(`/interaction/${id}`, { trigger: true });

    return {
      className: 'toast-action-lg',
      timeout: 8000,
      actionLabel: hasAction ? 'View Interaction' : undefined,
      onAction: hasAction ? actionCallback : undefined,
    };
  };

  static fetchRules = async () => {
    try {
      const triggers = await Api.UGC.FIELD_TRIGGERS.get();
      const rules = triggers?.rules || [];
      // Filter out rules that are not related to proposals
      return rules.filter((rule) => rule.fields.some((field) => field.field_name.startsWith('interaction')));
    } catch (error) {
      console.error('Error fetching Field Trigger rules:', error);
      return [];
    }
  };

  /**
   * This method maps the standard fields configuration defined in DNA into the new FieldTrigger rules format.
   * @param {*} standardFieldsConfig -- the DNA Config Object for standard fields, which may include "required" and "max_length" properties.
   * @param {*} isSummaryRequired -- a boolean indicating whether the summary field is required or not. For some reason, this is not included in the standardFieldsConfig object.
   * @returns an array of rules that can be used in the FieldTrigger system to validate the standard fields.
   */
  static mapDnaToFieldTriggerRules = (standardFieldsConfig = {}, isSummaryRequired) => {
    const dnaRules = Object.entries(standardFieldsConfig || {})
      .map(([key, configObject = {}]) => {
        const isRequired = configObject?.required || false;
        if (isRequired) {
          // Map related_proposal config key to the actual field name proposal_id
          const fieldName = key === 'related_proposal' ? 'proposal_id' : key;
          return {
            name: `DNA Standard Field Rule: ${key}`,
            rule_order: Number.MAX_SAFE_INTEGER, // Ensure these rules are at the end (highest priority)
            description: `${fromSnakeToTitleCase(key)} is required, per your organization's DNA configuration.`,
            condition: {
              if: [true, 'required'],
            },
            fields: [
              {
                field_name: `interaction.${fieldName}`,
              },
            ],
          };
        } else return null;
      })
      .filter((field) => field !== null);

    // Add the summary field rule
    if (isSummaryRequired) {
      dnaRules.push({
        name: 'DNA Standard Field Rule: Summary',
        rule_order: Number.MAX_SAFE_INTEGER, // Ensure these rules are at the end
        description: "Summary is required, per your organization's DNA configuration.",
        condition: {
          if: [true, 'required'],
        },
        fields: [
          {
            field_name: 'interaction.summary',
          },
        ],
      });
    }

    const otherRules = [
      {
        name: 'Status Field Rule',
        description: 'Hide Status Field unless org has status labels',
        condition: {
          if: [
            {
              '!': [{ var: 'interaction.has_status_labels' }],
            },
            'hidden',
            'visible',
          ],
        },
        rule_order: 1,
        fields: [
          {
            field_name: 'interaction.status',
          },
        ],
      },
      {
        name: 'Hide Interaction Type without permission',
        description: 'If the user does not have "Interaction Write" permission, hide the interaction type field',
        condition: {
          if: [
            {
              '!': [{ var: 'interaction.has_interaction_writes' }],
            },
            'hidden',
            'visible',
          ],
        },
        rule_order: Number.MAX_SAFE_INTEGER,
        fields: [
          {
            field_name: 'interaction.interaction_type',
          },
        ],
      },
    ];
    return [...dnaRules, ...otherRules];
  };

  /**
   * maps over custom fields configured for the organization and returns an array of rules that can be used in the FieldTrigger system to validate custom fields.
   * Each rule will have a name, description, condition, rule_order, and fields.
   * @param {boolean} hasCustomFields -- whether the organization has custom fields configured
   * @param {Array} orgCustomFields -- an array of custom fields configured for the organization, each field should have a "displayName" and "id" property.
   * @returns Array of rules that can be used in the FieldTrigger system to validate custom fields.
   */
  static mapCustomFieldsToRules = (hasCustomFields, orgCustomFields = []) => {
    if (!hasCustomFields || !orgCustomFields || orgCustomFields.length === 0) {
      return [];
    }

    return orgCustomFields
      .map((field) => {
        const { displayName, id, required } = field || {};
        if (required) {
          const fieldName = `interaction.custom_field.${id}`;
          return {
            name: `Custom Field Rule: ${displayName}`,
            description: `${displayName} is required, per your organization's custom fields configuration.`,
            condition: {
              if: [true, 'required'],
            },
            rule_order: Number.MIN_SAFE_INTEGER, // Ensure these rules are "before" the DNA rules (lowest priority)
            fields: [
              {
                field_name: fieldName,
              },
            ],
          };
        } else return null;
      })
      .filter((field) => field !== null);
  };

  /**
   * This method returns an amalgamation of rules that are used to validate the interaction form that are NOT handled explicitly by the FieldTrigger rules.
   * At the moment, this includes the maximum number of secondary targets and solicitors allowed for an interaction, as well as any "max_length" rules defined in the DNA standard fields config.
   * @param {*} secondaryTargetLimit - The maximum number of secondary targets allowed for an interaction (this is defined in DNA)
   * @param {*} interactionSolicitorLimit - The maximum number of solicitors allowed for an interaction (this is defined in DNA)
   * @param {*} interactionStandardFieldsConfig - The standard fields configuration from DNA, which may include certain rules like "max_length" for certain fields.
   * @returns {Object} - An object containing the additional rules for the interaction form.
   * @example
   * InteractionFormController.getAdditionalRules(5, 3, {
   *   field1: { max_length: 100 },
   *   field2: { max_length: 200 },
   * });
   * Returns:
   * {
   *   secondary_target: { max_length: 5 },
   *   solicitor: { max_length: 3 },
   *   field1: { max_length: 100 },
   *   field2: { max_length: 200 },
   * }
   */
  static getAdditionalRules = (
    secondaryTargetLimit,
    interactionSolicitorLimit,
    interactionStandardFieldsConfig,
    interactionDescriptionRichTextEditor
  ) => {
    // Grab the "max_length" rules from the interactionStandardFieldsConfig
    const maxLengthConfig = Object.entries(interactionStandardFieldsConfig || {}).reduce((acc, [key, value]) => {
      if (value?.max_length) {
        acc[key] = {
          max_length: value.max_length,
          message: `${fromSnakeToTitleCase(key)} must have fewer than ${
            value.max_length
          } characters, per your organization's DNA configuration.`,
          isRichText: interactionDescriptionRichTextEditor && key === 'text',
        };
      }
      return acc;
    }, {});

    // Add the secondary target and solicitor limits to the maxLengthConfig if they are defined

    if (secondaryTargetLimit) {
      maxLengthConfig.secondary_target = {
        max_length: secondaryTargetLimit,
        message: `Must have fewer than ${secondaryTargetLimit} additional constituents, per your organization's DNA configuration.`,
      };
    }

    if (interactionSolicitorLimit) {
      maxLengthConfig.solicitor = {
        max_length: interactionSolicitorLimit,
        message: `Must have fewer than ${interactionSolicitorLimit} solicitors, per your organization's DNA configuration.`,
      };
    }
    // Return the combined rules
    return maxLengthConfig;
  };

  // There are a few fields we need to modify from an existing interaction for editing purposes
  static parseExistingInteraction = (interaction = {}) => {
    const { label, custom_field, secondary_target, ...rest } = interaction;
    return {
      ...rest,
      label: Object.fromEntries(
        Object.entries(
          (label || []).reduce((acc, item) => {
            const group = acc[item.name] || [];
            group.push({ value: item.value, label: item.value });
            acc[item.name] = group;
            return acc;
          }, {})
        ).map(([key, val]) => [key, val[0]])
      ),
      custom_field: (custom_field || []).reduce((accum, field) => {
        accum[field.id] = field.value;
        return accum;
      }, {}),
      secondary_target: secondary_target || [],
    };
  };

  // This is the default interaction object that will be used when creating a new interaction
  // It is used to set the initial values for the form
  static defaultInteraction = (hasCustomFields, orgCustomFields, hasInteractionWrites) => {
    const customFields =
      hasCustomFields && orgCustomFields
        ? orgCustomFields.reduce((acc, field) => {
            acc[field.id] = ''; // Default value for custom fields is an empty string
            return acc;
          }, {})
        : {};
    const now = getDateInTimezone().getTime();
    return {
      interaction_type: hasInteractionWrites ? '' : this.commentType, // if the user has interactionWrites permission, let them pick. Else, lock them into Comment
      secondary_target: [],
      substantive: false,
      summary: '',
      text: '',
      date_occurred: now,
      label: {},
      custom_field: customFields,
      solicitor: [],
      ask_date: now, // dxo ask only
      ask_amount: null, // dxo ask only
      pinned: false, // comments only
      contact_mention: [], // comments only
      user_mention: [], // comments only
      purpose: '',
      proposal_id: null,
    };
  };
}
